import { Component, ChangeDetectionStrategy, booleanAttribute, input } from '@angular/core';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

/**
 * Bouton IDRA — composant à sélecteur d'attribut sur le <button> natif :
 * le type, le disabled et l'accessibilité restent natifs.
 *
 * Usage : <button idra-button variant="ghost" icon="pi pi-sign-out">Déconnexion</button>
 */
@Component({
  selector: 'button[idra-button]',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.primary]': "variant() === 'primary'",
    '[class.secondary]': "variant() === 'secondary'",
    '[class.ghost]': "variant() === 'ghost'",
    '[class.danger]': "variant() === 'danger'",
    '[attr.disabled]': 'disabled() || loading() ? true : null',
    '[attr.aria-busy]': 'loading()',
  },
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px 16px;
      border: 1px solid transparent;
      border-radius: var(--idra-radius-sm);
      font-family: inherit;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease,
        box-shadow 0.15s ease;
    }

    :host(.primary) {
      background: var(--idra-primary);
      color: var(--idra-primary-contrast);
    }
    :host(.primary:hover:not(:disabled)) {
      background: var(--idra-primary-hover);
      box-shadow: var(--idra-shadow-primary);
    }
    :host(.primary:active:not(:disabled)) {
      background: var(--idra-primary-active);
    }

    :host(.secondary) {
      background: var(--idra-surface-card);
      border-color: var(--idra-border-strong);
      color: var(--idra-text);
    }
    :host(.secondary:hover:not(:disabled)) {
      background: var(--idra-surface-hover);
    }

    :host(.ghost) {
      background: transparent;
      color: var(--idra-text-secondary);
    }
    :host(.ghost:hover:not(:disabled)) {
      background: var(--idra-surface-hover);
      color: var(--idra-text);
    }

    :host(.danger) {
      background: var(--idra-danger);
      color: #fff;
    }
    :host(.danger:hover:not(:disabled)) {
      background: var(--idra-danger-strong);
    }

    :host(:disabled) {
      opacity: 0.55;
      cursor: not-allowed;
    }

    :host(:focus-visible) {
      outline: none;
      box-shadow: var(--idra-focus-ring);
    }
  `,
  template: `
    @if (loading()) {
      <i class="pi pi-spinner pi-spin" aria-hidden="true"></i>
    } @else if (icon()) {
      <i [class]="icon()" aria-hidden="true"></i>
    }
    <ng-content />
  `,
})
export class IdraButton {
  readonly variant = input<ButtonVariant>('primary');
  readonly icon = input<string | undefined>(undefined);
  readonly loading = input(false);
  /**
   * Désactivation pilotée par binding. Input (et non propriété native) car le
   * host binding `[attr.disabled]` (état loading) retirerait l'attribut posé
   * par un `[disabled]` template — les deux états sont combinés ici.
   */
  readonly disabled = input(false, { transform: booleanAttribute });
}
